import { useTenant, useAnalytics, ConfigSetting } from '@parcel2go/npm-p2g-app';
import { GeoLookupResponse } from '~/lib/types/edge-api-types';
import useSWR from 'swr';
import { EDGE_API } from '~/lib/constants';
import { useCallback, useEffect, useState } from 'react';
import { fetchWithTenantAnalytics } from '~/lib/fetch';
import { captureWarning } from '~/lib/metrics';

type UseLocationParams = {
  isoCode?: string;
  postcode?: string;
  useGeo?: boolean;
  ready?: boolean;
  onGeoError?: (error: GeolocationPositionError) => void;
};

export default function useLocation({
  postcode,
  ready = true,
  useGeo = false,
  onGeoError = undefined,
  isoCode = undefined
}: UseLocationParams) {
  const { track, session } = useAnalytics();
  const { tenant, settings } = useTenant();
  const [coordinates, setCoordinates] = useState<{ latitude: number; longitude: number }>();
  const [geoError, setGeoError] = useState<GeolocationPositionError>();
  const [permission, setPermission] = useState<PermissionState>();

  const { data, error, isLoading } = useSWR<GeoLookupResponse>(
    ready && postcode
      ? `${EDGE_API}/${tenant}/order-process/address/geo/${isoCode || settings[ConfigSetting.DEFAULT_ORIGIN_COUNTRY]}?query=${encodeURIComponent(postcode)}`
      : null,
    (url: string) =>
      fetchWithTenantAnalytics(tenant, settings, session, url)
        .then((res) => res.json())
        .catch(() => captureWarning('Failed to fetch location data', { section: 'geo' })),
    { revalidateOnFocus: false, revalidateOnReconnect: false, revalidateIfStale: true }
  );

  useEffect(() => {
    if (navigator?.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((p) => {
        setPermission(p.state);
      });
    }
  }, []);

  const setError = useCallback(
    (error: GeolocationPositionError) => {
      setGeoError(error);
      if (onGeoError) onGeoError(error);
      setCoordinates(undefined);
    },
    [onGeoError]
  );

  const updateCoordinates = useCallback(({ coords }: { coords: GeolocationCoordinates }) => {
    const { latitude, longitude } = coords;
    setCoordinates({
      latitude,
      longitude
    });
  }, []);

  useEffect(() => {
    if (useGeo && ((!postcode && ready) || error)) {
      navigator?.geolocation?.getCurrentPosition(updateCoordinates, setError, { maximumAge: 10 });
    }
  }, [setError, updateCoordinates, postcode, ready, error, useGeo]);

  useEffect(() => {
    if (data && data.success && data.result) {
      setCoordinates({
        latitude: data.result.latitude,
        longitude: data.result.longitude
      });
    }
  }, [data]);

  useEffect(() => {
    if (coordinates) {
      track({ type: 'location', payload: coordinates, persist: false });
    }
  }, [track, coordinates]);

  return {
    coordinates,
    loading: isLoading,
    geoError: geoError,
    permission: permission
  };
}
